import { JSX } from "react";

import { Flex, Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { RestockingPriorityTier } from "__graphql__/types";
import { RestockingProduct } from "flows/Inventory/flows/RestockingList/models/restockingItem/types";
import {
  WarningRoundIcon,
  WarningThreeIcon,
  WarningHexagonIcon,
  CircleCrossIcon,
} from "ui/Icons/Icons";
import { ProductFacing } from "ui/ProductFacing/ProductFacing";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { Shelf } from "ui/Shelf/Shelf";
import { Status } from "ui/Status/Status";
import { getShelfDetail } from "utils/item";

export function PriorityIndicator({
  priorityTier,
  size = "regular",
}: {
  priorityTier: RestockingPriorityTier | null;
  size?: "regular" | "small";
}) {
  const intl = useIntl();

  if (!priorityTier) {
    return null;
  }

  let color: string;
  let icon: JSX.Element;
  let label: string;

  switch (priorityTier) {
    case RestockingPriorityTier.high:
      color = "red.100";
      label = intl.formatMessage({
        id: "flows.inventory.restocking-list.high-priority-label",
      });
      icon = <WarningHexagonIcon boxSize="24px" color="grey.800" />;
      break;
    case RestockingPriorityTier.medium:
      color = "orange.100";
      label = intl.formatMessage({
        id: "flows.inventory.restocking-list.med-priority-label",
      });
      icon = <WarningThreeIcon boxSize="24px" color="grey.800" />;
      break;
    case RestockingPriorityTier.low:
      color = "veggie.100";
      label = intl.formatMessage({
        id: "flows.inventory.restocking-list.low-priority-label",
      });
      icon = <WarningRoundIcon boxSize="24px" color="grey.800" />;
      break;
    default:
      color = "grey.150";
      label = intl.formatMessage({
        id: "flows.inventory.restocking-list.no-priority-label",
      });
      icon = <CircleCrossIcon boxSize="24px" color="grey.800" />;
  }

  return (
    <Status
      label={size === "regular" ? label : ""}
      bgColor={color}
      icon={icon}
      textColor="grey.800"
    />
  );
}

type RestockingListCardProps = Pick<
  RestockingProduct,
  | "name"
  | "imageUrl"
  | "stockOnShelf"
  | "shelf"
  | "numberOfShelfFacings"
  | "numberOfShelfStackings"
  | "isShelvedInHandlingUnits"
> & {
  isRestocked: boolean;
  priorityTier: RestockingPriorityTier | null;
};

export function RestockingListCard({
  name,
  imageUrl,
  stockOnShelf,
  shelf,
  numberOfShelfFacings,
  numberOfShelfStackings,
  isShelvedInHandlingUnits,
  priorityTier = null,
  isRestocked,
}: RestockingListCardProps) {
  const { shelfLetter, shelfNumber } = shelf
    ? getShelfDetail(shelf)
    : { shelfLetter: "", shelfNumber: "" };
  return (
    <Box
      w="full"
      bg="white"
      p="s200"
      borderRadius="sm"
      flexDirection="column"
      data-testid="restocking-list-item-card"
      filter={isRestocked ? "grayscale(100%)" : undefined}
    >
      <Flex position="relative" justifyContent="space-between" alignItems="center" mb="s200">
        {isRestocked ? (
          <Status label="Restocked" bgColor="veggie.100" textColor="grey.800" />
        ) : (
          <PriorityIndicator priorityTier={priorityTier} />
        )}
        {shelfLetter && shelfNumber && (
          <Shelf letter={shelfLetter} number={shelfNumber} ml="auto" />
        )}
      </Flex>
      <ProductInformation
        bg="grey.100"
        name={name ?? ""}
        imageUrl={imageUrl ?? ""}
        showSKU={false}
        stock={stockOnShelf}
        showProductFacing={false}
        showLeftSideBar={isRestocked}
        leftSideBarProps={{ bgColor: "veggie.500" }}
      />
      {numberOfShelfFacings !== null && (
        <Box
          mt="s100"
          borderBottomLeftRadius="sm"
          borderBottomRightRadius="sm"
          bg="grey.100"
          p="s100"
        >
          <ProductFacing
            withTopSpacing={false}
            numberOfFacings={numberOfShelfFacings}
            numberOfStacking={numberOfShelfStackings}
            isShelvedInHandlingUnits={isShelvedInHandlingUnits}
          />
        </Box>
      )}
    </Box>
  );
}
